// Data and Format features that live in pop-ups and the side panel, following Sheets: filters, data validation
// (dropdowns), conditional formatting, named ranges and functions, protected sheets and ranges, column statistics.
import { colToIndex, functionNames, indexToCol } from '../../engine/src/index.ts';
import type { Scalar } from '../../engine/src/index.ts';
import type { Format, Op } from '../../shared/ops.ts';
import { protections } from '../../shared/protect.ts';
import type { Protection } from '../../shared/protect.ts';
import { api } from './api.ts';
import type { Me, Member } from './api.ts';
import * as cmd from './commands.ts';
import type { Range } from './grid.ts';
import { CONDITIONS, condLabel, ruleId } from './rules.ts';
import type { CfRule, Cond, CondType, DvRule, DvType, FilterSpec, ScalePoint } from './rules.ts';
import { closeMenus, dialog, h, isLightColor, popup, promptText, toast } from './ui.ts';
import type { MenuItem } from './ui.ts';

export interface PanelCtx extends cmd.Ctx {
  docId: string; user: Me; role(): string; canEdit(): boolean;
  openPanel(title: string, body: HTMLElement): void; closePanel(): void;
  switchSheet(id: number): void;
  refresh(): void;                 // redraw the editor chrome (filter view bar, toolbar state)
}
const MAX_ROW = 999_999;
type SheetRange = Range & { sheet: number };

// ----- range text -----
/** "A1:B5", "B:B", "3:4", "'시트 2'!A1:C9" -> a range on a sheet (the current one when not named). */
export function parseRange(x: PanelCtx, text: string): SheetRange | null {
  let t = text.trim().replace(/^=/, ''), sheet = x.sheet();
  const bang = t.lastIndexOf('!');
  if (bang > 0) {
    const name = t.slice(0, bang).replace(/^'(.*)'$/, '$1').replace(/''/g, "'");
    const s = x.model.sheets.find(q => q.name.toLowerCase() === name.toLowerCase()); if (!s) return null;
    sheet = s.id; t = t.slice(bang + 1);
  }
  const part = (p: string): { r: number | null; c: number | null } | null => {
    const m = /^\$?([A-Z]{1,3})?\$?(\d+)?$/i.exec(p); if (!m || (!m[1] && !m[2])) return null;
    return { c: m[1] ? colToIndex(m[1]) : null, r: m[2] ? Number(m[2]) - 1 : null };
  };
  const [a, b] = t.split(':'), p = part(a), q = b === undefined ? p : part(b);
  if (!p || !q) return null;
  const r1 = Math.min(p.r ?? 0, q.r ?? 0), r2 = Math.max(p.r ?? MAX_ROW, q.r ?? MAX_ROW);
  const c1 = Math.min(p.c ?? 0, q.c ?? 0), c2 = Math.max(p.c ?? 18_277, q.c ?? 18_277);
  if (r1 < 0 || c1 < 0) return null;
  return { sheet, r1, c1, r2: p.r === null && q.r === null ? MAX_ROW : r2, c2: p.c === null && q.c === null ? 18_277 : c2 };
}
export function rangeText(x: PanelCtx, R: Range, sheet = x.sheet(), withSheet = false): string {
  const cell = (r: number, c: number): string => `${indexToCol(c)}${r + 1}`;
  let body: string;
  if (R.r1 === 0 && R.r2 >= MAX_ROW) body = `${indexToCol(R.c1)}:${indexToCol(R.c2)}`;
  else if (R.c1 === 0 && R.c2 >= 18_277) body = `${R.r1 + 1}:${R.r2 + 1}`;
  else body = R.r1 === R.r2 && R.c1 === R.c2 ? cell(R.r1, R.c1) : `${cell(R.r1, R.c1)}:${cell(R.r2, R.c2)}`;
  if (!withSheet) return body;
  const name = x.model.sheetName(sheet) ?? '';
  return `${/^[\p{L}_][\p{L}\p{N}_]*$/u.test(name) ? name : `'${name.replace(/'/g, "''")}'`}!${body}`;
}

// small form helpers
const field = (label: string, ...control: (Node | string)[]): HTMLElement => h('label', { class: 'pf' }, h('span', { class: 'pf-label' }, label), ...control);
const select = (options: [string, string][], value?: string, onchange?: () => void): HTMLSelectElement =>
  h('select', { class: 'pf-input', onchange }, ...options.map(([v, l]) => h('option', { value: v, selected: v === value }, l)));
const input = (value = '', placeholder = ''): HTMLInputElement => h('input', { class: 'pf-input', value, placeholder });
const buttons = (...b: HTMLElement[]): HTMLElement => h('div', { class: 'pf-buttons' }, ...b);
const btn = (label: string, onclick: () => void, primary = false): HTMLButtonElement => h('button', { class: primary ? 'primary' : 'pf-btn', onclick }, label);
const COLORS = ['#e8eaed', '#fce8e6', '#fef7e0', '#e6f4ea', '#e8f0fe', '#f3e8fd', '#ffd7d2', '#ffe5a0', '#c6efce', '#bfe1f6', '#e6cff2', '#d4edbc'];

/** Text inputs for a condition's values, shown or hidden with the chosen condition. */
function condEditor(cond: Cond | undefined, types: CondType[] = CONDITIONS.map(c => c.type)): { el: HTMLElement; read(): Cond } {
  const a = input(cond?.a ?? '', '값 또는 수식'), b = input(cond?.b ?? '', '값 또는 수식');
  const sel = select(types.map(t => [t, condLabel(t)]), cond?.type ?? types[0], () => sync());
  const sync = (): void => {
    const n = CONDITIONS.find(c => c.type === sel.value)?.args ?? 0;
    a.hidden = n < 1; b.hidden = n < 2;
    a.placeholder = sel.value === 'custom' ? '예: =$B1>100' : sel.value.startsWith('date') ? '예: 2026-03-01 또는 today' : '값 또는 수식';
  };
  sync();
  return { el: h('div', { class: 'col' }, sel, a, b), read: () => ({ type: sel.value as CondType, ...(a.hidden ? {} : { a: a.value }), ...(b.hidden ? {} : { b: b.value }) }) };
}

// =====================================================================================================
// Filter
// =====================================================================================================
/** The data block around a single cell (Ctrl+A in Sheets): grows while neighbouring rows/columns have data. */
export function dataRegion(x: PanelCtx, R: Range): Range {
  if (R.r1 !== R.r2 || R.c1 !== R.c2) return R;
  const s = x.sheet(), filled = (r: number, c: number): boolean => r >= 0 && c >= 0 && x.model.display(s, r, c).kind !== 'empty';
  let { r1, c1, r2, c2 } = R;
  for (let grew = true; grew;) {
    grew = false;
    // through the cells along the block's edges, not its corners (as Sheets' Ctrl+A grows it)
    const rowHas = (r: number): boolean => { for (let c = c1; c <= c2; c++) if (filled(r, c)) return true; return false; };
    const colHas = (c: number): boolean => { for (let r = r1; r <= r2; r++) if (filled(r, c)) return true; return false; };
    if (r1 > 0 && rowHas(r1 - 1)) { r1--; grew = true; }
    if (rowHas(r2 + 1)) { r2++; grew = true; }
    if (c1 > 0 && colHas(c1 - 1)) { c1--; grew = true; }
    if (colHas(c2 + 1)) { c2++; grew = true; }
  }
  return { r1, c1, r2, c2 };
}
/** Save the filter in effect: the open filter view, or the sheet's filter. */
function setCurrentFilter(x: PanelCtx, f: FilterSpec): void {
  const vid = x.model.activeViews.get(x.sheet());
  if (vid) {
    const views = ((x.model.sheet(x.sheet())?.props.filterViews ?? []) as (FilterSpec & { id: string })[]).map(v => (v.id === vid ? { ...v, ...f } : v));
    cmd.setProps(x, { filterViews: views });
  } else cmd.setProps(x, { filter: f });
}
export function toggleFilter(x: PanelCtx): void {
  if (x.model.activeViews.get(x.sheet())) { x.model.activeViews.delete(x.sheet()); x.model.version++; history.replaceState(null, '', location.pathname + location.search); x.refresh(); }
  if (x.model.sheet(x.sheet())?.props.filter) { cmd.setProps(x, { filter: null }); return; }
  const selected = x.grid.range;
  const R = selected.r1 === selected.r2 && selected.c1 === selected.c2 ? dataRegion(x, selected) : selected;
  cmd.setProps(x, { filter: { r1: R.r1, c1: R.c1, r2: R.r2, c2: R.c2, criteria: {} } });
}
/** Right-click > 셀 값으로 필터링: create a filter if there is none and show only the rows matching the cell's value. */
export function filterByCellValue(x: PanelCtx): void {
  const s = x.sheet(), { r, c } = x.grid.sel;
  let f = x.model.sheet(s)?.props.filter as { r1: number; c1: number; r2: number; c2: number; criteria?: Record<string, unknown> } | undefined;
  if (!f) {
    const selected = x.grid.range;
    const R = selected.r1 === selected.r2 && selected.c1 === selected.c2 ? dataRegion(x, selected) : selected;
    f = { r1: R.r1, c1: R.c1, r2: R.r2, c2: R.c2, criteria: {} };
  }
  if (c < f.c1 || c > f.c2) return;
  const want = x.model.display(s, r, c).text, last = f.r2, hide = new Set<string>();
  for (let i = f.r1 + 1; i <= last; i++) { const t = x.model.display(s, i, c).text; if (t !== want) hide.add(t); }
  cmd.setProps(x, { filter: { ...f, criteria: { ...(f.criteria ?? {}), [c]: { hide: [...hide] } } } });
}
/** The filter button's pop-up: sort, filter by condition, filter by values. */
export function openFilter(x: PanelCtx, col: number, px: number, py: number): void {
  const s = x.sheet(), f = x.model.currentFilter(s); if (!f) return;
  const last = f.r2, crit = f.criteria?.[col] ?? {};
  const counts = new Map<string, number>();
  for (let r = f.r1 + 1; r <= last; r++) { const t = x.model.display(s, r, col).text; counts.set(t, (counts.get(t) ?? 0) + 1); }
  const values = [...counts.keys()].sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b, 'ko', { numeric: true })));
  const hidden = new Set(crit.hide ?? []);
  const search = input('', '');
  search.setAttribute('aria-label', '검색');
  const list = h('div', { class: 'flt-list' }), shown = h('div', { class: 'muted small' });
  const boxes = new Map<string, HTMLInputElement>();
  // as in Sheets: just the values (no counts), "N개 표시 중" for what the search leaves
  const render = (): void => {
    const q = search.value.trim().toLowerCase(), vis = values.filter(v => !q || v.toLowerCase().includes(q));
    shown.textContent = `${vis.length}개 표시 중`;
    list.replaceChildren(...vis.map(v => {
      const cb = boxes.get(v) ?? h('input', { type: 'checkbox', checked: !hidden.has(v) }); boxes.set(v, cb);
      return h('label', { class: 'flt-item' }, cb, h('span', {}, v === '' ? '(공백)' : v));
    }));
  };
  search.addEventListener('input', render); render();
  const setAll = (on: boolean): void => { for (const v of values) { const cb = boxes.get(v) ?? h('input', { type: 'checkbox' }); cb.checked = on; boxes.set(v, cb); } };
  const cond = condEditor(crit.cond, ['empty', 'notEmpty', 'textContains', 'textNotContains', 'textStarts', 'textEnds', 'textEq', 'dateEq', 'dateBefore', 'dateAfter', 'gt', 'gte', 'lt', 'lte', 'eq', 'neq', 'between', 'notBetween', 'custom']);
  const useCond = h('input', { type: 'checkbox', checked: !!crit.cond });
  const body: Range = { r1: f.r1 + 1, c1: f.c1, r2: last, c2: f.c2 }, sortRows = (asc: boolean): void => { closeMenus(); cmd.sortRange(x, body, [{ col, asc }]); };
  const apply = (): void => {
    const hide = values.filter(v => boxes.get(v) && !boxes.get(v)!.checked);
    const next = { ...(f.criteria ?? {}) };
    const c: { hide?: string[]; cond?: Cond; color?: { kind: 'bg' | 'color'; value: string | null } } = {};
    if (hide.length) c.hide = hide;
    if (useCond.checked) c.cond = cond.read();
    if (crit.color) c.color = crit.color;
    if (Object.keys(c).length) next[col] = c; else delete next[col];
    setCurrentFilter(x, { r1: f.r1, c1: f.c1, r2: f.r2, c2: f.c2, criteria: next });
    closeMenus(); x.grid.el.focus();
  };
  // 색상을 기준으로 정렬 / 색상별 필터링: the fill and text colours used in the column
  const colorsOf = (kind: 'bg' | 'color'): string[] => [...new Set(Array.from({ length: last - f.r1 }, (_, i) => String(x.model.fmt(s, f.r1 + 1 + i, col)?.[kind] ?? '')).filter(Boolean))];
  const colorItems = (kind: 'bg' | 'color', pick: (c: string | null) => void, withNone: boolean): MenuItem[] => {
    const cs = colorsOf(kind);
    return cs.length || withNone ? [...cs.map(c => ({ content: h('div', { class: 'menu-item flt-color', onclick: () => { closeMenus(); pick(c); } }, h('span', { class: 'flt-swatch', style: { background: c } }), h('span', { class: 'menu-label' }, c)) })), ...(withNone ? [{ label: '없음', action: () => pick(null) }] : [])] : [{ label: '색상 없음', disabled: true }];
  };
  const byColor = (kind: 'bg' | 'color', c: string | null): void => {
    const next = { ...(f.criteria ?? {}), [col]: { ...(f.criteria?.[col] ?? {}), color: { kind, value: c } } };
    setCurrentFilter(x, { r1: f.r1, c1: f.c1, r2: f.r2, c2: f.c2, criteria: next }); x.grid.el.focus();
  };
  const sub = (label: string, items: () => MenuItem[]): HTMLElement => {
    const row = h('div', { class: 'menu-item' }, h('span', { class: 'menu-check' }), h('span', { class: 'menu-label' }, label), h('span', { class: 'menu-key' }, '▸'));
    row.addEventListener('mouseenter', () => { const r = row.getBoundingClientRect(); popup(items(), r.right - 2, r.top - 4, 1); });
    return row;
  };
  const item = (label: string, fn: () => void): HTMLElement => h('div', { class: 'menu-item', onmouseenter: () => closeAbove(), onclick: fn }, h('span', { class: 'menu-check' }), h('span', { class: 'menu-label' }, label), h('span', { class: 'menu-key' }));
  const closeAbove = (): void => { document.querySelectorAll('.menu-pop').forEach((m, i) => { if (i > 0) m.remove(); }); };
  const pop = h('div', { class: 'flt-pop' },
    item('정렬, 오름차순', () => sortRows(true)), item('정렬, 내림차순', () => sortRows(false)),
    sub('색상을 기준으로 정렬', () => [{ label: '채우기 색상', items: colorItems('bg', c => { closeMenus(); if (c) cmd.sortByColor(x, body, col, 'bg', c); }, false) }, { label: '텍스트 색상', items: colorItems('color', c => { closeMenus(); if (c) cmd.sortByColor(x, body, col, 'color', c); }, false) }]),
    sub('색상별 필터링', () => [{ label: '채우기 색상', items: colorItems('bg', c => byColor('bg', c), true) }, { label: '텍스트 색상', items: colorItems('color', c => byColor('color', c), true) }]),
    h('div', { class: 'menu-sep' }),
    h('details', { open: !!crit.cond }, h('summary', {}, '조건별 필터링'), h('label', { class: 'row' }, useCond, ' 조건 사용'), cond.el),
    h('details', { open: !crit.cond }, h('summary', {}, '값별 필터링'),
      h('div', { class: 'row' }, h('button', { class: 'link', onclick: () => { setAll(true); render(); } }, `${values.length}개 모두 선택`), ' - ', h('button', { class: 'link', onclick: () => { setAll(false); render(); } }, '지우기')),
      shown, search, list),
    buttons(btn('취소', () => closeMenus()), btn('확인', apply, true)));
  popup(pop, px, py);
}


// =====================================================================================================
// Data validation
// =====================================================================================================
const DV_TYPES: [DvType, string][] = [
  ['list', '드롭다운'], ['range', '드롭다운(범위)'],
  ...CONDITIONS.filter(c => c.type !== 'empty' && c.type !== 'notEmpty' && c.type !== 'custom').map(c => [c.type, c.label] as [DvType, string]),
  ['email', '유효한 이메일인 텍스트'], ['url', '유효한 URL인 텍스트'], ['date', '날짜 유효함'], ['checkbox', '체크박스'], ['custom', '맞춤 수식'],
];
const dvLabel = (r: DvRule): string => (DV_TYPES.find(t => t[0] === r.type)?.[1] ?? r.type) + (r.type === 'list' ? `: ${(r.values ?? []).join(', ')}` : r.type === 'range' ? `: ${r.source}` : r.a ? ` ${r.a}${r.b ? ` ~ ${r.b}` : ''}` : '');
const dvRules = (x: PanelCtx): DvRule[] => (x.model.sheet(x.sheet())?.props.validation ?? []) as DvRule[];

export function validationPanel(x: PanelCtx, edit?: DvRule | 'new'): void {
  if (edit) { validationForm(x, edit === 'new' ? undefined : edit); return; }
  const rules = dvRules(x);
  x.openPanel('데이터 확인 규칙', h('div', { class: 'panel' },
    ...rules.map(r => h('div', { class: 'rule-card', onclick: () => validationForm(x, r) }, h('div', { class: 'rule-range' }, rangeText(x, r)), h('div', { class: 'muted' }, dvLabel(r)))),
    rules.length ? '' : h('p', { class: 'muted' }, '이 시트에는 데이터 확인 규칙이 없습니다.'),
    x.canEdit() ? h('button', { class: 'pf-add', onclick: () => validationForm(x) }, '+규칙 추가') : ''));
}
function validationForm(x: PanelCtx, rule?: DvRule): void {
  // a new dropdown rejects other input, as in Sheets
  const R = rule ?? { ...x.grid.range, id: ruleId(), type: 'list' as DvType, values: ['옵션 1', '옵션 2'], style: 'chip' as const, reject: true };
  const where = input(rangeText(x, R));
  const type = select(DV_TYPES.map(([v, l]) => [v, l]), R.type, () => sync());
  // dropdown items with colours
  const items = h('div', { class: 'col' });
  const addItem = (v = '', color = ''): void => {
    const t = input(v, '값'), c = select([['', '기본'], ...COLORS.slice(1).map((k, i) => [k, `색상 ${i + 1}`] as [string, string])], color);
    c.style.background = color || '';
    c.addEventListener('change', () => { c.style.background = c.value; });
    items.append(h('div', { class: 'row dv-item' }, t, c, h('button', { class: 'pf-btn', title: '삭제', onclick: (e: Event) => (e.currentTarget as HTMLElement).parentElement!.remove() }, '✕')));
  };
  (R.values ?? []).forEach((v, i) => addItem(v, R.colors?.[i] ?? ''));
  const addBtn = h('button', { class: 'link', onclick: () => addItem() }, '다른 항목 추가');
  const multi = h('input', { type: 'checkbox', checked: !!R.multi });
  const source = input(R.source ?? '', '예: A1:A10 또는 시트2!A1:A20');
  const a = input(R.a ?? '', '값 또는 수식'), b = input(R.b ?? '', '값 또는 수식');
  const help = h('input', { type: 'checkbox', checked: !!R.help }), helpText = input(R.help ?? '', '도움말 텍스트');
  const warn = h('input', { type: 'radio', name: 'dvbad', checked: !R.reject }), reject = h('input', { type: 'radio', name: 'dvbad', checked: !!R.reject });
  const style = select([['chip', '칩'], ['arrow', '화살표'], ['plain', '일반 텍스트']], R.style ?? 'chip');
  const listBox = h('div', { class: 'col' }, items, addBtn), rangeBox = field('범위', source), multiBox = h('label', { class: 'row' }, multi, ' 다중 선택 허용'), argBox = h('div', { class: 'col' }, a, b), styleBox = field('표시 스타일', style);
  const sync = (): void => {
    const t = type.value as DvType, n = CONDITIONS.find(c => c.type === t)?.args ?? (t === 'custom' ? 1 : 0);
    listBox.hidden = t !== 'list'; rangeBox.hidden = t !== 'range'; styleBox.hidden = t !== 'list' && t !== 'range'; multiBox.hidden = styleBox.hidden;
    argBox.hidden = n === 0; a.hidden = n < 1; b.hidden = n < 2;
    a.placeholder = t === 'custom' ? '예: =LEN(A1)<=10' : '값 또는 수식';
    helpText.hidden = !help.checked;
  };
  help.addEventListener('change', sync); sync();
  const save = (): void => {
    const where2 = parseRange(x, where.value); if (!where2 || where2.sheet !== x.sheet()) { toast('범위가 올바르지 않습니다'); return; }
    const t = type.value as DvType;
    const next: DvRule = { id: R.id, r1: where2.r1, c1: where2.c1, r2: Math.min(where2.r2, MAX_ROW), c2: Math.min(where2.c2, 18_277), type: t };
    if (t === 'list') {
      const rows = [...items.querySelectorAll('.dv-item')].map(e => { const [i, c] = e.querySelectorAll<HTMLInputElement | HTMLSelectElement>('input, select'); return [i.value.trim(), c.value] as const; }).filter(([v]) => v);
      if (!rows.length) { toast('항목을 하나 이상 입력하세요'); return; }
      next.values = rows.map(r => r[0]); if (rows.some(r => r[1])) next.colors = rows.map(r => r[1]);
    }
    if (t === 'range') { if (!parseRange(x, source.value)) { toast('범위가 올바르지 않습니다'); return; } next.source = source.value.trim(); }
    if (!a.hidden) next.a = a.value; if (!b.hidden) next.b = b.value;
    if (t === 'list' || t === 'range') { next.style = style.value as DvRule['style']; if (multi.checked) next.multi = true; }
    if (help.checked && helpText.value.trim()) next.help = helpText.value.trim();
    if (reject.checked) next.reject = true;
    const others = dvRules(x).filter(o => o.id !== R.id);
    const ops: Op[] = [{ t: 'props', sheet: x.sheet(), patch: { validation: [...others, next] } }];
    if (t === 'checkbox') {
      // checkboxes render from the checkbox number format, empty cells start unchecked
      const used = x.model.used(x.sheet()), r2 = Math.min(next.r2, Math.max(next.r1, used.rows + 100)), c2 = Math.min(next.c2, Math.max(next.c1, used.cols + 5));
      ops.push({ t: 'fmt', sheet: x.sheet(), r1: next.r1, c1: next.c1, r2, c2, fmt: { nf: 'checkbox' } });
      for (let r = next.r1; r <= r2; r++) for (let c = next.c1; c <= c2; c++) if (!/^(TRUE|FALSE)$/i.test(x.model.raw(x.sheet(), r, c))) ops.push({ t: 'set', sheet: x.sheet(), r, c, raw: 'FALSE' });
    }
    x.submit({ t: 'batch', ops });
    validationPanel(x);
  };
  const remove = (): void => { x.submit({ t: 'props', sheet: x.sheet(), patch: { validation: dvRules(x).filter(o => o.id !== R.id) } }); validationPanel(x); };
  x.openPanel('데이터 확인 규칙', h('div', { class: 'panel' },
    field('범위에 적용', where), field('기준', type), listBox, rangeBox, multiBox, argBox,
    h('details', { class: 'pf-adv', open: !!(R.help || R.reject) }, h('summary', {}, '고급 옵션'),
      h('label', { class: 'row' }, help, ' 선택한 셀의 도움말 텍스트 표시'), helpText,
      h('div', { class: 'pf-label' }, '데이터가 잘못된 경우:'), h('label', { class: 'row' }, warn, ' 경고 표시'), h('label', { class: 'row' }, reject, ' 입력 거부'),
      styleBox),
    buttons(btn('규칙 삭제', rule ? remove : () => validationPanel(x)), btn('완료', save, true))));
}
/** Click on a dropdown cell: the list of choices (with 다중 선택 허용, tick boxes that keep the list open). */
export function openDropdown(x: PanelCtx, r: number, c: number, px: number, py: number): void {
  const s = x.sheet(), rule = x.model.validationAt(s, r, c); if (!rule) return;
  const cur = x.model.display(s, r, c).text;
  if (rule.multi) {
    const chosen = new Set(cur.split(',').map(v => v.trim()).filter(Boolean)), all = x.model.listValues(s, rule);
    const boxes = all.map(v => h('label', { class: 'flt-item' }, h('input', { type: 'checkbox', checked: chosen.has(v), onchange: (e: Event) => {
      if ((e.target as HTMLInputElement).checked) chosen.add(v); else chosen.delete(v);
      const text = all.filter(a => chosen.has(a)).join(', ');
      x.submit({ t: 'set', sheet: s, r, c, raw: text || null });
    } }), h('span', {}, v)));
    popup(h('div', { class: 'flt-pop' }, ...(boxes.length ? boxes : [h('p', { class: 'muted' }, '선택 가능한 항목이 없습니다')])), px, py);
    return;
  }
  const items: MenuItem[] = x.model.listValues(s, rule).map(v => ({ label: v, checked: v === cur, action: () => { x.submit({ t: 'set', sheet: s, r, c, raw: v }); x.grid.el.focus(); } }));
  if (!items.length) items.push({ label: '선택 가능한 항목이 없습니다', disabled: true });
  if (cur) items.push({ separator: true }, { label: '지우기', action: () => x.submit({ t: 'set', sheet: s, r, c, raw: null }) });
  popup(items, px, py);
}
/** Reject input that breaks a "reject" rule; returns the ops that may go through (null = all refused). */
export function enforceValidation(x: PanelCtx, op: Op): Op | null {
  const bad: string[] = [];
  const check = (o: Op): Op | null => {
    if (o.t === 'batch') { const ops = o.ops.map(check).filter((p): p is Op => p !== null); return ops.length ? { t: 'batch', ops } : null; }
    if (o.t !== 'set' || o.raw === null) return o;
    const rule = x.model.validationAt(o.sheet, o.r, o.c); if (!rule?.reject) return o;
    const name = x.model.sheetName(o.sheet)!;
    let v: Scalar;
    if (o.raw.startsWith('=')) { const e = x.model.wb.evaluateFormula(o.raw, name, o.r, o.c); v = Array.isArray(e) ? (e[0]?.[0] ?? null) : (e as Scalar); }
    else v = rawValue(o.raw);
    if (x.model.isValid(o.sheet, o.r, o.c, v)) return o;
    bad.push(`${indexToCol(o.c)}${o.r + 1}`);
    return null;
  };
  const out = check(op);
  if (bad.length) toast(`입력한 데이터가 ${bad.slice(0, 3).join(', ')}${bad.length > 3 ? ' 등' : ''} 셀의 데이터 확인 규칙을 위반합니다`, 5000);
  return out;
}
function rawValue(raw: string): Scalar {
  const t = raw.trim();
  if (/^(true|false)$/i.test(t)) return t.toUpperCase() === 'TRUE';
  if (t !== '' && !Number.isNaN(Number(t.replace(/,/g, '')))) return Number(t.replace(/,/g, ''));
  return raw;
}

// =====================================================================================================
// Conditional formatting
// =====================================================================================================
const cfRules = (x: PanelCtx): CfRule[] => (x.model.sheet(x.sheet())?.props.cf ?? []) as CfRule[];
const STYLE_PRESETS: Format[] = [
  { bg: '#b7e1cd' }, { bg: '#fce8b2' }, { bg: '#f4c7c3' }, { color: '#0b8043', bg: '#b7e1cd' }, { color: '#b45f06', bg: '#fce8b2' }, { color: '#cc0000', bg: '#f4c7c3' },
  { b: true, color: '#cc0000' }, { b: true, color: '#0b8043' },
];
const SCALES: [string, string, string][] = [['#57bb8a', '#ffd666', '#e67c73'], ['#e67c73', '#ffd666', '#57bb8a'], ['#ffffff', '', '#57bb8a'], ['#ffffff', '', '#e67c73'], ['#ffffff', '', '#4285f4'], ['#4285f4', '#ffffff', '#e67c73']];
// a light fill keeps dark text in dark mode too, as the cell itself shows it (grid.ts)
const preview = (f: Format, text = '123'): HTMLElement => h('span', { class: 'cf-preview', style: { background: String(f.bg ?? 'transparent'), color: String(f.color ?? (f.bg && isLightColor(String(f.bg)) ? '#1f1f1f' : 'inherit')), fontWeight: f.b ? '700' : '400', fontStyle: f.i ? 'italic' : 'normal', textDecoration: [f.u ? 'underline' : '', f.s ? 'line-through' : ''].join(' ').trim() || 'none' } }, text);

export function cfPanel(x: PanelCtx, fresh = false): void {
  const rules = cfRules(x);
  if (fresh && !rules.length && x.canEdit()) { cfForm(x); return; }
  const move = (i: number, d: number): void => { const n = [...rules]; const [r] = n.splice(i, 1); n.splice(i + d, 0, r); cmd.setProps(x, { cf: n }); cfPanel(x); };
  x.openPanel('조건부 서식 규칙', h('div', { class: 'panel' },
    ...rules.map((r, i) => h('div', { class: 'rule-card' },
      h('div', { class: 'row', onclick: () => cfForm(x, r) },
        r.kind === 'scale' ? h('span', { class: 'cf-preview', style: { background: `linear-gradient(90deg, ${r.scale!.min.color}, ${r.scale!.mid?.type !== 'none' && r.scale!.mid ? r.scale!.mid.color + ', ' : ''}${r.scale!.max.color})` } }, '') : preview(r.style ?? {}),
        h('div', { class: 'grow' }, h('div', {}, r.kind === 'scale' ? '색상 스케일' : condLabel(r.cond!.type) + (r.cond!.a ? ` ${r.cond!.a}` : '') + (r.cond!.b ? ` ~ ${r.cond!.b}` : '')), h('div', { class: 'muted' }, rangeText(x, r)))),
      x.canEdit() ? h('div', { class: 'rule-order' }, h('button', { class: 'pf-btn', disabled: i === 0, title: '위로', onclick: () => move(i, -1) }, '▲'), h('button', { class: 'pf-btn', disabled: i === rules.length - 1, title: '아래로', onclick: () => move(i, 1) }, '▼')) : '')),
    rules.length ? h('p', { class: 'muted small' }, '규칙은 위에서부터 적용되며, 먼저 일치하는 규칙의 서식이 사용됩니다.') : h('p', { class: 'muted' }, '이 시트에는 조건부 서식 규칙이 없습니다.'),
    x.canEdit() ? h('button', { class: 'pf-add', onclick: () => cfForm(x) }, '+다른 규칙 추가') : ''));
}
export function cfForm(x: PanelCtx, rule?: CfRule): void {
  const R: CfRule = rule ?? { ...x.grid.range, id: ruleId(), kind: 'single', cond: { type: 'notEmpty' }, style: { ...STYLE_PRESETS[0] } };
  const where = input(rangeText(x, R));
  let kind: CfRule['kind'] = R.kind;
  const tabs = h('div', { class: 'pf-tabs' });
  const body = h('div', { class: 'col' });
  // single colour
  const cond = condEditor(R.cond);
  const style: Format = { ...(R.style ?? STYLE_PRESETS[0]) };
  const stylePreview = h('div', { class: 'cf-sample' });
  const toggles = h('div', { class: 'row' });
  const color = (key: 'color' | 'bg', label: string): HTMLElement => { const i = h('input', { type: 'color', value: String(style[key] ?? (key === 'color' ? '#000000' : '#ffffff')), oninput: () => { style[key] = i.value; paint(); } }); return h('label', { class: 'cf-color', title: label }, label, i); };
  const paint = (): void => {
    stylePreview.replaceChildren(preview(style, '미리보기'));
    toggles.replaceChildren(...(['b', 'i', 'u', 's'] as const).map(k => h('button', { class: `pf-btn${style[k] ? ' on' : ''}`, onclick: () => { if (style[k]) delete style[k]; else style[k] = true; paint(); } }, { b: '굵게', i: '기울임', u: '밑줄', s: '취소선' }[k])), color('color', '글자색 '), color('bg', '채우기 '));
  };
  paint();
  const presets = h('div', { class: 'row wrap' }, ...STYLE_PRESETS.map(p => h('button', { class: 'cf-preset', onclick: () => { for (const k of Object.keys(style)) delete style[k]; Object.assign(style, p); paint(); } }, preview(p, '가'))));
  const single = h('div', { class: 'col' }, h('div', { class: 'pf-label' }, '다음의 경우 셀 서식 지정…'), cond.el, h('div', { class: 'pf-label' }, '서식 지정 스타일'), presets, stylePreview, toggles);
  // colour scale
  const sc = R.scale ?? { min: { type: 'min', color: '#ffffff' }, max: { type: 'max', color: '#57bb8a' } };
  const point = (label: string, p: ScalePoint | undefined, def: ScalePoint): { el: HTMLElement; read(): ScalePoint } => {
    const t = select([['min', '최소값'], ['max', '최대값'], ['num', '숫자'], ['pct', '백분율'], ['percentile', '백분위수'], ...(label === '중간값' ? [['none', '없음'] as [string, string]] : [])], (p ?? def).type, () => { v.hidden = !['num', 'pct', 'percentile'].includes(t.value); });
    const v = input((p ?? def).v ?? '', '값'), c = h('input', { type: 'color', value: (p ?? def).color });
    v.hidden = !['num', 'pct', 'percentile'].includes(t.value);
    return { el: field(label, h('div', { class: 'row' }, t, v, c)), read: () => ({ type: t.value as ScalePoint['type'], ...(v.hidden ? {} : { v: v.value }), color: c.value }) };
  };
  const pMin = point('최소값', sc.min, { type: 'min', color: '#ffffff' }), pMid = point('중간값', sc.mid, { type: 'none', color: '#ffd666' }), pMax = point('최대값', sc.max, { type: 'max', color: '#57bb8a' });
  const scalePresets = h('div', { class: 'row wrap' }, ...SCALES.map(([a, m, b]) => h('button', { class: 'cf-preset', onclick: () => {
    (pMin.el.querySelector('input[type=color]') as HTMLInputElement).value = a; (pMax.el.querySelector('input[type=color]') as HTMLInputElement).value = b;
    const mt = pMid.el.querySelector('select') as HTMLSelectElement; mt.value = m ? 'pct' : 'none'; mt.dispatchEvent(new Event('change'));
    if (m) { (pMid.el.querySelector('input[type=color]') as HTMLInputElement).value = m; (pMid.el.querySelector('input.pf-input') as HTMLInputElement).value = '50'; }
  } }, h('span', { class: 'cf-preview', style: { background: `linear-gradient(90deg, ${a}, ${m ? m + ', ' : ''}${b})` } }, ''))));
  const scale = h('div', { class: 'col' }, h('div', { class: 'pf-label' }, '미리보기 및 사전 설정'), scalePresets, pMin.el, pMid.el, pMax.el);
  const renderKind = (): void => {
    tabs.replaceChildren(...(['single', 'scale'] as const).map(k => h('button', { class: `pf-tab${kind === k ? ' on' : ''}`, onclick: () => { kind = k; renderKind(); } }, k === 'single' ? '단색' : '색상 스케일')));
    body.replaceChildren(kind === 'single' ? single : scale);
  };
  renderKind();
  const save = (): void => {
    const w = parseRange(x, where.value); if (!w || w.sheet !== x.sheet()) { toast('범위가 올바르지 않습니다'); return; }
    const base = { id: R.id, r1: w.r1, c1: w.c1, r2: Math.min(w.r2, MAX_ROW), c2: Math.min(w.c2, 18_277) };
    const next: CfRule = kind === 'single' ? { ...base, kind, cond: cond.read(), style: { ...style } } : { ...base, kind, scale: { min: pMin.read(), ...(pMid.read().type === 'none' ? {} : { mid: pMid.read() }), max: pMax.read() } };
    const list = cfRules(x), i = list.findIndex(o => o.id === R.id);
    cmd.setProps(x, { cf: i >= 0 ? list.map(o => (o.id === R.id ? next : o)) : [...list, next] });
    cfPanel(x);
  };
  const remove = (): void => { const left = cfRules(x).filter(o => o.id !== R.id); cmd.setProps(x, { cf: left.length ? left : null }); cfPanel(x); };
  x.openPanel('조건부 서식 규칙', h('div', { class: 'panel' }, tabs, field('범위에 적용', where), body,
    buttons(rule ? btn('규칙 삭제', remove) : btn('취소', () => cfPanel(x)), btn('완료', save, true)),
    h('button', { class: 'pf-add', onclick: () => { save(); cfForm(x); } }, '+다른 규칙 추가')));
}

// =====================================================================================================
// Named ranges and named functions (document level)
// =====================================================================================================
type NamedMap = Record<string, Range & { sheet: number; deleted?: boolean }>;
const named = (x: PanelCtx): NamedMap => (x.model.props.namedRanges ?? {}) as NamedMap;
const nameProblem = (name: string, taken: string[]): string | null => {
  if (!/^[\p{L}_][\p{L}\p{N}_.]*$/u.test(name)) return '이름은 문자나 밑줄로 시작하고 문자, 숫자, 밑줄, 마침표만 포함할 수 있습니다';
  if (/^[A-Z]{1,3}\d+$/i.test(name) || /^R\d*C\d*$/i.test(name) || /^(TRUE|FALSE)$/i.test(name)) return '셀 참조와 같은 이름은 사용할 수 없습니다';
  if (name.length > 250) return '이름이 너무 깁니다';
  if (taken.some(t => t.toLowerCase() === name.toLowerCase())) return '이미 사용 중인 이름입니다';
  return null;
};
export function namedRangesPanel(x: PanelCtx, add = false): void {
  const map = named(x);
  const form = (old?: string): void => {
    const cur = old ? map[old] : undefined;
    const name = input(old ?? '', '이름'), where = input(cur ? rangeText(x, cur, cur.sheet, true) : rangeText(x, x.grid.range, x.sheet(), true));
    const save = (): void => {
      const n = name.value.trim(), problem = nameProblem(n, Object.keys(map).filter(k => k !== old));
      if (problem) { toast(problem); return; }
      const R = parseRange(x, where.value); if (!R) { toast('범위가 올바르지 않습니다'); return; }
      const next: NamedMap = { ...map }; if (old) delete next[old];
      next[n] = { sheet: R.sheet, r1: R.r1, c1: R.c1, r2: Math.min(R.r2, MAX_ROW), c2: Math.min(R.c2, 18_277) };
      x.submit({ t: 'docprops', patch: { namedRanges: next } });
      namedRangesPanel(x);
    };
    const remove = (): void => { const next = { ...map }; delete next[old!]; x.submit({ t: 'docprops', patch: { namedRanges: Object.keys(next).length ? next : null } }); namedRangesPanel(x); };
    x.openPanel('이름이 지정된 범위', h('div', { class: 'panel' }, field('이름', name), field('범위', where),
      buttons(old ? btn('범위 삭제', remove) : btn('취소', () => namedRangesPanel(x)), btn('완료', save, true))));
    name.focus();
  };
  x.openPanel('이름이 지정된 범위', h('div', { class: 'panel' },
    ...Object.entries(map).sort(([a], [b]) => a.localeCompare(b)).map(([n, v]) => h('div', { class: 'rule-card' },
      h('div', { class: 'row' },
        h('div', { class: 'grow', onclick: () => { if (v.deleted) return; if (v.sheet !== x.sheet()) x.switchSheet(v.sheet); x.grid.selectRange({ r1: v.r1, c1: v.c1, r2: Math.min(v.r2, x.grid.rowCount() - 1), c2: Math.min(v.c2, x.grid.colCount() - 1) }); } },
          h('b', {}, n), h('div', { class: 'muted' }, v.deleted ? '#REF!' : rangeText(x, v, v.sheet, true))),
        x.canEdit() ? h('button', { class: 'pf-btn', onclick: () => form(n) }, '수정') : ''))),
    Object.keys(map).length ? '' : h('p', { class: 'muted' }, '이름이 지정된 범위가 없습니다. 수식에서 범위 대신 이름을 쓸 수 있습니다 (예: =SUM(매출)).'),
    x.canEdit() ? h('button', { class: 'pf-add', onclick: () => form() }, '+ 범위 추가') : ''));
  if (add && x.canEdit()) form();
}
type FnMap = Record<string, { params: string[]; body: string; desc?: string }>;
export function namedFunctionsPanel(x: PanelCtx): void {
  const map = (x.model.props.namedFunctions ?? {}) as FnMap;
  const form = (old?: string): void => {
    const cur = old ? map[old] : undefined;
    const name = input(old ?? '', '예: TAX'), desc = input(cur?.desc ?? '', '설명 (선택사항)'), params = input((cur?.params ?? []).join(', '), '예: price, rate'), body = h('textarea', { class: 'pf-input', rows: 3, placeholder: '예: =price*rate' }, cur?.body ?? '');
    const save = (): void => {
      const n = name.value.trim().toUpperCase();
      if (!/^[A-Z][A-Z0-9_.]*$/.test(n)) { toast('함수 이름은 영문자로 시작하고 영문자, 숫자, 밑줄, 마침표만 쓸 수 있습니다'); return; }
      if (functionNames().includes(n)) { toast('기본 제공 함수와 같은 이름은 사용할 수 없습니다'); return; }
      if (Object.keys(map).some(k => k !== old && k === n)) { toast('이미 사용 중인 이름입니다'); return; }
      const ps = params.value.split(',').map(p => p.trim()).filter(Boolean);
      if (ps.some(p => !/^[\p{L}_][\p{L}\p{N}_]*$/u.test(p) || /^[A-Z]{1,3}\d+$/i.test(p))) { toast('인수 이름이 올바르지 않습니다'); return; }
      if (!body.value.trim()) { toast('수식 정의를 입력하세요'); return; }
      const next: FnMap = { ...map }; if (old) delete next[old];
      next[n] = { params: ps, body: body.value.trim().startsWith('=') ? body.value.trim() : `=${body.value.trim()}`, ...(desc.value.trim() ? { desc: desc.value.trim() } : {}) };
      x.submit({ t: 'docprops', patch: { namedFunctions: next } });
      namedFunctionsPanel(x);
    };
    const remove = (): void => { const next = { ...map }; delete next[old!]; x.submit({ t: 'docprops', patch: { namedFunctions: Object.keys(next).length ? next : null } }); namedFunctionsPanel(x); };
    x.openPanel('이름이 지정된 함수', h('div', { class: 'panel' }, field('함수 이름', name), field('함수 설명', desc), field('인수 자리표시자', params), field('수식 정의', body),
      buttons(old ? btn('삭제', remove) : btn('취소', () => namedFunctionsPanel(x)), btn('완료', save, true))));
  };
  x.openPanel('이름이 지정된 함수', h('div', { class: 'panel' },
    ...Object.entries(map).map(([n, f]) => h('div', { class: 'rule-card' }, h('div', { class: 'row' },
      h('div', { class: 'grow' }, h('b', {}, `${n}(${f.params.join(', ')})`), h('div', { class: 'muted' }, f.desc ?? f.body)),
      x.canEdit() ? h('button', { class: 'pf-btn', onclick: () => form(n) }, '수정') : ''))),
    Object.keys(map).length ? '' : h('p', { class: 'muted' }, '수식을 이름이 있는 함수로 저장해 이 문서 어디서나 쓸 수 있습니다 (예: =TAX(A2, 0.1)).'),
    x.canEdit() ? h('button', { class: 'pf-add', onclick: () => form() }, '+ 함수 추가') : ''));
}

// =====================================================================================================
// Protected sheets and ranges
// =====================================================================================================
export async function protectPanel(x: PanelCtx, add?: 'range' | 'sheet'): Promise<void> {
  const list = protections(x.model.sheet(x.sheet())?.props);
  x.openPanel('보호된 시트 및 범위', h('div', { class: 'panel' },
    ...list.map(p => h('div', { class: 'rule-card', onclick: () => protectForm(x, p) },
      h('b', {}, p.desc || (p.sheet ? x.model.sheetName(x.sheet()) ?? '' : rangeText(x, p))),
      h('div', { class: 'muted' }, `${p.sheet ? '시트 전체' : rangeText(x, p)} · ${p.warn ? '경고만 표시' : `수정 가능: ${p.editors.length ? `${p.editors.length}명` : '소유자만'}`}`))),
    list.length ? '' : h('p', { class: 'muted' }, '보호된 범위가 없습니다. 범위나 시트를 보호하면 지정한 사용자만 수정할 수 있습니다.'),
    x.canEdit() ? h('button', { class: 'pf-add', onclick: () => protectForm(x) }, '+ 시트 또는 범위 추가') : ''));
  if (add && x.canEdit()) protectForm(x, undefined, add);
}
async function protectForm(x: PanelCtx, p?: Protection, startMode?: 'range' | 'sheet'): Promise<void> {
  const R = x.grid.range;
  const cur: Protection = p ?? { id: ruleId(), r1: R.r1, c1: R.c1, r2: R.r2, c2: R.c2, editors: [x.user.id] };
  const desc = input(cur.desc ?? '', '설명 입력');
  let mode: 'range' | 'sheet' = startMode ?? (cur.sheet ? 'sheet' : 'range');
  const where = input(cur.sheet ? '' : rangeText(x, cur));
  const exceptOn = h('input', { type: 'checkbox', checked: !!cur.except?.length }), exceptText = input((cur.except ?? []).map(e => rangeText(x, e)).join(', '), '예: A1:B10, D3');
  const tabs = h('div', { class: 'pf-tabs' }), target = h('div', { class: 'col' });
  const renderMode = (): void => {
    tabs.replaceChildren(...(['range', 'sheet'] as const).map(k => h('button', { class: `pf-tab${mode === k ? ' on' : ''}`, onclick: () => { mode = k; renderMode(); } }, k === 'range' ? '범위' : '시트')));
    target.replaceChildren(mode === 'range' ? field('범위', where) : h('div', { class: 'col' }, h('div', {}, x.model.sheetName(x.sheet()) ?? ''), h('label', { class: 'row' }, exceptOn, ' 특정 셀 제외'), exceptText));
  };
  renderMode();
  const warn = h('input', { type: 'radio', name: 'pmode', checked: !!cur.warn }), restrict = h('input', { type: 'radio', name: 'pmode', checked: !cur.warn });
  const who = select([['me', '나만'], ['custom', '맞춤']], cur.editors.length <= 1 && (cur.editors[0] ?? x.user.id) === x.user.id ? 'me' : 'custom', () => { people.hidden = who.value !== 'custom'; });
  const people = h('div', { class: 'col prot-people' });
  people.hidden = who.value !== 'custom';
  try {
    const r = await api<{ members: Member[] }>('GET', `/api/docs/${x.docId}/members`);
    people.replaceChildren(...r.members.filter(m => m.role === 'editor' || m.role === 'owner').map(m => h('label', { class: 'row' },
      h('input', { type: 'checkbox', value: String(m.id), checked: m.role === 'owner' || cur.editors.includes(Number(m.id)), disabled: m.role === 'owner' || Number(m.id) === x.user.id }),
      ` ${m.display_name ?? m.username}${m.role === 'owner' ? ' (소유자)' : ''}`)));
  } catch { people.replaceChildren(h('p', { class: 'muted' }, '공동작업자 목록을 불러오지 못했습니다')); }
  const save = (): void => {
    const next: Protection = { id: cur.id, r1: 0, c1: 0, r2: MAX_ROW, c2: 18_277, editors: [] };
    if (mode === 'range') { const w = parseRange(x, where.value); if (!w || w.sheet !== x.sheet()) { toast('범위가 올바르지 않습니다'); return; } Object.assign(next, { r1: w.r1, c1: w.c1, r2: Math.min(w.r2, MAX_ROW), c2: Math.min(w.c2, 18_277) }); }
    else {
      next.sheet = true;
      if (exceptOn.checked && exceptText.value.trim()) {
        const ex = exceptText.value.split(',').map(t => parseRange(x, t)); if (ex.some(e => !e)) { toast('제외할 범위가 올바르지 않습니다'); return; }
        next.except = ex.map(e => ({ r1: e!.r1, c1: e!.c1, r2: Math.min(e!.r2, MAX_ROW), c2: Math.min(e!.c2, 18_277) }));
      }
    }
    if (desc.value.trim()) next.desc = desc.value.trim();
    if (warn.checked) next.warn = true;
    else next.editors = who.value === 'me' ? [x.user.id] : [...new Set([x.user.id, ...[...people.querySelectorAll<HTMLInputElement>('input:checked')].map(i => Number(i.value))])];
    const list = protections(x.model.sheet(x.sheet())?.props), i = list.findIndex(o => o.id === cur.id);
    cmd.setProps(x, { protected: i >= 0 ? list.map(o => (o.id === cur.id ? next : o)) : [...list, next] });
    protectPanel(x);
  };
  const remove = (): void => { const left = protections(x.model.sheet(x.sheet())?.props).filter(o => o.id !== cur.id); cmd.setProps(x, { protected: left.length ? left : null }); protectPanel(x); };
  x.openPanel('보호된 시트 및 범위', h('div', { class: 'panel' }, field('설명', desc), tabs, target,
    h('div', { class: 'pf-label' }, '범위 수정 권한'),
    h('label', { class: 'row' }, warn, ' 이 범위를 수정할 때 경고 표시'),
    h('label', { class: 'row' }, restrict, ' 이 범위를 수정할 수 있는 사용자 제한'), who, people,
    buttons(p ? btn('보호 해제', remove) : btn('취소', () => protectPanel(x)), btn('완료', save, true))));
}

// =====================================================================================================
// Column statistics
// =====================================================================================================
export function columnStats(x: PanelCtx, col = x.grid.sel.c): void {
  const s = x.sheet(), used = x.model.used(s);
  const nums: number[] = [], freq = new Map<string, number>();
  let filled = 0, empty = 0;
  for (let r = 0; r < used.rows; r++) {
    const d = x.model.display(s, r, col);
    if (d.kind === 'empty') { empty++; continue; }
    filled++; freq.set(d.text, (freq.get(d.text) ?? 0) + 1);
    const v = x.model.value(s, r, col); if (typeof v === 'number') nums.push(v);
  }
  nums.sort((a, b) => a - b);
  const sum = nums.reduce((a, b) => a + b, 0), fmt = (n: number): string => (Number.isFinite(n) ? Number(n.toPrecision(10)).toLocaleString('ko-KR', { maximumFractionDigits: 6 }) : '-');
  const median = nums.length ? (nums.length % 2 ? nums[(nums.length - 1) / 2] : (nums[nums.length / 2 - 1] + nums[nums.length / 2]) / 2) : NaN;
  const top = [...freq.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10), maxF = top[0]?.[1] ?? 1;
  const row = (k: string, v: string): HTMLElement => h('tr', {}, h('td', { class: 'muted' }, k), h('td', { class: 'num' }, v));
  x.openPanel('열 통계', h('div', { class: 'panel' },
    h('div', { class: 'row' }, h('button', { class: 'pf-btn', disabled: col === 0, onclick: () => columnStats(x, col - 1) }, '◀'), h('b', { class: 'grow center' }, `${indexToCol(col)}열`), h('button', { class: 'pf-btn', onclick: () => columnStats(x, col + 1) }, '▶')),
    h('div', { class: 'pf-label' }, '빈도'),
    h('div', { class: 'freq' }, ...top.map(([v, n]) => h('div', { class: 'freq-row' }, h('span', { class: 'freq-label' }, v), h('span', { class: 'freq-bar', style: { width: `${(n / maxF) * 100}%` } }), h('span', { class: 'muted' }, String(n))))),
    h('div', { class: 'pf-label' }, '요약'),
    h('table', { class: 'kv stats' }, row('행 수', fmt(filled + empty)), row('비어 있음', fmt(empty)), row('고유 값', fmt(freq.size)),
      ...(nums.length ? [row('합계', fmt(sum)), row('평균', fmt(sum / nums.length)), row('중앙값', fmt(median)), row('최소값', fmt(nums[0])), row('최대값', fmt(nums[nums.length - 1]))] : []))));
}

// =====================================================================================================
// Selection summary (bottom right: 합계 / 평균 / ...)
// =====================================================================================================
export function selectionStats(x: PanelCtx): { label: string; value: string }[] | null {
  const R = x.grid.range, s = x.sheet(), used = x.model.used(s);
  if (R.r1 === R.r2 && R.c1 === R.c2) return null;
  let count = 0, numCount = 0, sum = 0, min = Infinity, max = -Infinity;
  for (let r = R.r1; r <= Math.min(R.r2, used.rows - 1); r++) for (let c = R.c1; c <= Math.min(R.c2, used.cols - 1); c++) {
    const v = x.model.value(s, r, c);
    if (v === null || v === '') continue;
    count++; if (typeof v === 'number') { numCount++; sum += v; min = Math.min(min, v); max = Math.max(max, v); }
  }
  if (!count) return null;
  const f = (n: number): string => Number(n.toPrecision(12)).toLocaleString('ko-KR', { maximumFractionDigits: 10 });
  const out = [{ label: '개수', value: f(count) }];
  if (numCount) out.unshift({ label: '합계', value: f(sum) }, { label: '평균', value: f(sum / numCount) }, { label: '최소', value: f(min) }, { label: '최대', value: f(max) }, { label: '숫자 개수', value: f(numCount) });
  return out;
}

// =====================================================================================================
// Version history (File > Version history)
// =====================================================================================================
interface Version { id: number; seq: number; name: string | null; created_at: string; author: string | null }
export async function nameVersion(x: PanelCtx, then?: () => void): Promise<void> {
  const name = (await promptText('현재 버전 이름 지정', new Date().toLocaleString('ko-KR'), '버전 이름'))?.trim();
  if (!name) return;
  await api('POST', `/api/docs/${x.docId}/versions`, { name: name.slice(0, 200) });
  toast('버전 이름을 지정했습니다');
  then?.();
}
export async function versionsPanel(x: PanelCtx, showChanges: () => void, namedOnly = false): Promise<void> {
  const list = (await api<{ versions: Version[] }>('GET', `/api/docs/${x.docId}/versions`)).versions.filter(v => !namedOnly || v.name);
  const restore = async (v: Version): Promise<void> => {
    if (!await dialog('이 버전 복원', h('p', {}, `${v.name ?? new Date(v.created_at).toLocaleString('ko-KR')} 버전으로 되돌릴까요? 현재 내용은 버전 기록에 남습니다.`), [{ label: '취소', value: false }, { label: '복원', value: true, primary: true }])) return;
    await api('POST', `/api/docs/${x.docId}/versions/${v.id}/restore`);
  };
  const rename = async (v: Version): Promise<void> => {
    const name = await promptText('버전 이름 지정', v.name ?? '', '버전 이름'); if (name === null) return;
    await api('PATCH', `/api/docs/${x.docId}/versions/${v.id}`, { name: name.trim() || null });
    versionsPanel(x, showChanges, namedOnly);
  };
  x.openPanel('버전 기록', h('div', { class: 'panel' },
    h('label', { class: 'row' }, h('input', { type: 'checkbox', checked: namedOnly, onchange: (e: Event) => versionsPanel(x, showChanges, (e.target as HTMLInputElement).checked) }), ' 이름이 지정된 버전만 표시'),
    x.canEdit() ? h('button', { class: 'pf-add', onclick: () => nameVersion(x, () => versionsPanel(x, showChanges, namedOnly)) }, '+ 현재 버전 이름 지정') : '',
    ...list.map(v => h('div', { class: 'rule-card version' },
      h('b', {}, v.name ?? new Date(v.created_at).toLocaleString('ko-KR')),
      h('div', { class: 'muted small' }, `${v.name ? new Date(v.created_at).toLocaleString('ko-KR') + ' · ' : ''}${v.author ?? ''}`),
      h('div', { class: 'row' },
        h('button', { class: 'pf-btn', onclick: () => window.open(`/d/${x.docId}?version=${v.id}`, '_blank') }, '미리보기'),
        x.canEdit() ? h('button', { class: 'pf-btn', onclick: () => restore(v) }, '이 버전 복원') : '',
        x.canEdit() ? h('button', { class: 'pf-btn', onclick: () => rename(v) }, '이름 지정') : ''))),
    list.length ? '' : h('p', { class: 'muted' }, namedOnly ? '이름이 지정된 버전이 없습니다.' : '아직 저장된 버전이 없습니다. 편집하는 동안 30분마다 자동으로 저장되며, 이름을 지정해 지금 버전을 남길 수도 있습니다.'),
    h('button', { class: 'link', onclick: showChanges }, '세부 변경 내역 보기 (최근 200개 변경)')));
}
